import { booleanAttribute, ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { debounceTime, merge, startWith, Subject, Subscription, takeUntil } from 'rxjs';
import { AutoCompleteSelectionItem } from '../../../core/component/autocomplete-remote/autocomplete-selection-item';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { SelectionItem } from '../../../core/component/select/select.component';
import { getMonthName } from '../../../core/helper/month';
import { LocalDate } from '../../../core/model/local-date';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { ENGLISH_CULTURE, SWEDISH_CULTURE } from '../../../core/service/language.service';
import { LettingAdvertisment } from '../../model/letting-advertisment';
import { LettingShowing } from '../../model/letting-showing';
import { DhMarketOptions } from '../../model/platform-options';
import { LettingAdvertismentOrdinaryService } from '../../service/letting-advertisment-ordinary.service';

@Component({
  selector: 'letting-advertisment-market',
  templateUrl: 'letting-advertisment-market.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class LettingAdvertismentMarketComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) letting: LettingAdvertisment;
  @Input({ required: true }) marketOptions: DhMarketOptions;
  @Input({ transform: booleanAttribute }) readonly: boolean = false;
  @Input({ transform: booleanAttribute }) allowBoplatsSverigePublish: boolean = false;
  @Input() set tenants(val: LettingShowing[]) {
    if (val && Array.isArray(val)) {
      this._tenants = val;
      this.tenantSelection = [new SelectionItem('COMMON.NOBODY', null)].concat(val.map(t => new SelectionItem(t.name, t.id)));
    } else {
      this._tenants = [];
      this.tenantSelection = [];
    }
  }

  public form: ReturnType<typeof this.createForm>;

  protected featuresForm: FormArray;
  protected excludedFeaturesForm: FormArray;
  protected tenantSelection: SelectionItem[];
  protected selectedOrdinary: LettingShowing;
  protected monthSelection: SelectionItem[] = Array(12)
    .fill(undefined)
    .map((_, i) => new SelectionItem(getMonthName(i + 1), i + 1));
  protected marketCultures = [SWEDISH_CULTURE, ENGLISH_CULTURE];
  protected defaultPublicationPeriodInDays: number;
  protected canSendSubscriptions: boolean = false;

  publishFromSubscription: Subscription | undefined;
  reqProfileIdSubscription: Subscription | undefined;

  private _tenants: LettingShowing[];
  private _destroy = new Subject<void>();

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly formFactory: FormControlExFactory,
    protected readonly ordinaryService: LettingAdvertismentOrdinaryService
  ) {}

  ordinaryChanged(event: AutoCompleteSelectionItem) {
    this.selectedOrdinary = event?.data;
  }

  ngOnInit(): void {
    this.form = this.createForm();
    this.form.controls.marketBoplatsSverigeReadiness.updateValueAndValidity();

    this.reqProfileIdSubscription = this.form.controls.dhMarketRequirementProfile.valueChanges.pipe(takeUntil(this._destroy)).subscribe({
      next: () => {
        this.defaultPublicationPeriodInDays = this.marketOptions.defaultPublicationPeriods.find(
          profile => profile.id === this.form.controls.dhMarketRequirementProfile.value
        ).defaultPublicationPeriodInDays;

        this.handlePublishFrom();
      },
    });

    this.publishFromSubscription = this.form.controls.publishFrom.valueChanges.pipe(takeUntil(this._destroy)).subscribe({
      next: () => {
        this.handlePublishFrom();
      },
    });

    if (this.letting.isNew && !this.form.controls.dhMarketRequirementProfile.value) {
      this.form.controls.dhMarketRequirementProfile.setValue(
        this.marketOptions?.requirementProfiles?.length > 0 ? this.marketOptions.requirementProfiles[0].value : null
      );
    }

    this.form.controls.showingTenantId.valueChanges.pipe(takeUntil(this._destroy)).subscribe({
      next: () => {
        if (!this.form.value.showingTenantText) {
          this.form.controls.showingTenantText.setValue(this.marketOptions.showingTenantText);
        }
      },
    });

    this.form.controls.showingOrdinaryId.valueChanges.pipe(takeUntil(this._destroy)).subscribe({
      next: () => {
        if (!this.form.value.showingOrdinaryText) {
          this.form.controls.showingOrdinaryText.setValue(this.marketOptions.showingOrdinaryText);
        }
      },
    });

    merge(this.form.controls.publishFrom.valueChanges, this.form.controls.publishTo.valueChanges)
      .pipe(startWith(null), debounceTime(200), takeUntil(this._destroy))
      .subscribe({
        next: () => {
          if (!this.form.value.publishFrom) {
            this.canSendSubscriptions = false;
            return;
          }

          const now = Date.now();
          const f = new Date(this.form.value.publishFrom).getTime();
          const t = this.form.value.publishTo ? new Date(this.form.value.publishTo).getTime() : null;

          this.canSendSubscriptions = f <= now && (t === null || t >= now);
        },
      });

    this.form.controls.marketPublishToBoplatsSverige.valueChanges.pipe(takeUntil(this._destroy)).subscribe({
      next: () => this.form.controls.marketBoplatsSverigeReadiness.updateValueAndValidity(),
    });

    merge(this.form.controls.publishFrom.valueChanges, this.form.controls.publishTo.valueChanges)
      .pipe(takeUntil(this._destroy))
      .subscribe({
        next: () => this.form.controls.marketBoplatsSverigeReadiness.updateValueAndValidity(),
      });
  }

  private createForm() {
    const today = new Date().toISOString();

    return this.formBuilder.group({
      dhMarketRequirementProfile: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.REQUIREMENT_PROFILE',
        this.letting.dhMarketRequirementProfile,
        {
          isReadonly: this.readonly,
          required: true,
        }
      ),
      vacancyFrom: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_FROM', this.letting.vacancyFrom, {
        required: true,
        isReadonly: this.readonly,
        validator: this.emptyLocalDateValidator,
      }),
      vacancyTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.VACANT_TO', this.letting.vacancyTo, {
        isReadonly: this.readonly,
      }),
      publishFrom: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.PUBLISH_FROM',
        this.letting.publishFrom ? this.letting.publishFrom : today,
        {
          required: true,
          isReadonly: this.readonly,
        }
      ),
      publishTo: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.PUBLISH_TO', this.letting.publishTo, {
        isReadonly: this.readonly,
      }),
      monthlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHLY_RENT', this.letting.monthlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      yearlyRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.YEARLY_RENT', this.letting.yearlyRent, {
        required: true,
        isReadonly: this.readonly,
      }),
      monthsWithRent: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.MONTHS_WITH_RENT', this.letting.monthsWithRent, {
        isReadonly: this.readonly,
      }),
      objectDescription: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.OBJECT_DESCRIPTION', this.letting.objectDescription, {
        isReadonly: this.readonly,
        required: true,
      }),
      uniqueObjectDescription: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DONT_UPDATE_DESCRIPTION',
        { value: this.letting.uniqueObjectDescription, disabled: this.readonly },
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.HELP_TEXT.DONT_UPDATE_DESCRIPTION_OBJECT' }
      ),
      includedLettingFeatureIds: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.INCLUDED_FEATURES_HEADER',
        this.letting.includedLettingFeatures.map(feature => feature.id),
        { isReadonly: this.readonly }
      ),
      notIncludedLettingFeatureIds: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.EXCLUDED_FEATURES_HEADER',
        this.letting.notIncludedLettingFeatures.map(feature => feature.id),
        { isReadonly: this.readonly }
      ),
      showingTenantId: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.DHMARKET.SHOWING_TENANT', this.letting.showingTenantId, {
        isReadonly: this.readonly,
      }),
      showingTenantText: this.formFactory.createWithOptions('LETTING.ADVERTISMENT.DHMARKET.SHOWING_TEXT', this.letting.showingTenantText, {
        isReadonly: this.readonly,
      }),
      showingOrdinaryId: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.SHOWING_ORDINARY_NAME',
        this.letting.showingOrdinaryId,
        { isReadonly: this.readonly }
      ),
      showingOrdinaryText: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.SHOWING_TEXT',
        this.letting.showingOrdinaryText,
        { isReadonly: this.readonly }
      ),
      marketSendSubscriptions: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.SEND_SUBSCRIPTIONS',
        this.letting.marketSendSubscriptions,
        { isReadonly: this.readonly, helpText: 'LETTING.ADVERTISMENT.DHMARKET.SEND_SUBSCRIPTIONS_HELP' }
      ),
      marketPublishToBoplatsSverige: this.formFactory.createWithOptions(
        'LETTING.ADVERTISMENT.DHMARKET.PUBLISH_TO_BOPLATS_SVERIGE',
        this.allowBoplatsSverigePublish ? this.letting.marketPublishToBoplatsSverige : false,
        { isReadonly: this.readonly }
      ),
      marketBoplatsSverigeReadiness: this.formFactory.create(
        'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.READINESS',
        null,
        this.boplatsSverigeReadinessValidator
      ),
    });
  }

  ngOnDestroy(): void {
    this._destroy.next();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.readonly && this.form) {
      this.applyReadonly(this.form);
    }
  }

  handlePublishFrom() {
    if (this.defaultPublicationPeriodInDays) {
      const publishUntil = new Date(this.form.controls.publishFrom.value);
      publishUntil.setUTCDate(publishUntil.getDate() + this.defaultPublicationPeriodInDays - 1);
      publishUntil.setUTCHours(23);
      publishUntil.setUTCMinutes(59 + publishUntil.getTimezoneOffset());
      this.form.controls.publishTo.setValue(publishUntil.toISOString());
    }
  }

  protected getTenant = (id: string) => this._tenants.find(t => t.id === id);

  private emptyLocalDateValidator: ValidatorFn = (control: AbstractControl<LocalDate>): { [key: string]: any } | null =>
    control.value?.year !== null && control.value?.month !== null && control.value?.day !== null ? null : { required: 'required' };

  public get missingBoplatsSverigePublishFields(): string[] {
    return [
      this.form?.controls.publishFrom.value ? null : 'LETTING.ADVERTISMENT.PUBLISH_FROM',
      this.form?.controls.publishTo.value ? null : 'LETTING.ADVERTISMENT.PUBLISH_TO',
      this.letting.boplatsSverigeLocation ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.LOCATION',
      this.letting.boplatsSverigeMunicipality ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.MUNICIPALITY',
      this.letting.boplatsSverigeLivingArea > 0 ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.LIVING_AREA',
      this.letting.boplatsSverigeRooms > 0 ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.ROOMS',
      this.letting.boplatsSverigeStreetAddress ? null : 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.REQUIRED_FIELDS.STREET_ADDRESS',
    ].filter((x): x is string => !!x);
  }

  private boplatsSverigeReadinessValidator: ValidatorFn = (): ValidationErrors | null =>
    this.allowBoplatsSverigePublish && this.form?.controls.marketPublishToBoplatsSverige.value && this.missingBoplatsSverigePublishFields.length
      ? {
          boplatsSverigeReadiness: {
            message: 'LETTING.ADVERTISMENT.BOPLATS_SVERIGE.ERRORS.MISSING_OBJECT_FIELDS',
          },
        }
      : null;

  private applyReadonly(control: AbstractControl): void {
    if (control instanceof FormGroup || control instanceof FormArray) {
      Object.keys(control.controls).forEach(key => this.applyReadonly(control.controls[key]));
      return;
    }

    if (control instanceof FormControlEx) {
      control.isReadonly = this.readonly;
    }

    control.disable();
    control.updateValueAndValidity({ emitEvent: false });
  }
}
